import { useMemo } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

// The names files were picked with, by source id, kept in this browser so a
// reload still shows "consult-2026-08-14.pdf" instead of the reader's title
// ("Previous note", staging check of #553). Never sent: a file name can
// identify the patient, so the assistant gets the file's title and id only.
// The newest SOURCE_NAMES_KEPT are kept.
export const SOURCE_NAMES_KEPT = 300;

interface SourceNamesState {
  names: Record<string, string>;
  setName: (id: number, name: string) => void;
  removeName: (id: number) => void;
}

export const useSourceNamesStore = create<SourceNamesState>()(
  persist(
    (set) => ({
      names: {},
      setName: (id, name) =>
        set(({ names }) => {
          const value = name.trim();
          if (!value) return { names };
          const { [String(id)]: _old, ...rest } = names;
          const entries = Object.entries({ ...rest, [String(id)]: value });
          return {
            names: Object.fromEntries(entries.slice(-SOURCE_NAMES_KEPT)),
          };
        }),
      removeName: (id) =>
        set(({ names }) => {
          if (!(String(id) in names)) return { names };
          const { [String(id)]: _gone, ...rest } = names;
          return { names: rest };
        }),
    }),
    {
      name: "rosc.sourceNames",
      storage: createJSONStorage(() => localStorage),
      partialize: ({ names }) => ({ names }),
    },
  ),
);

// The kept names as a map by source id.
export const useSourceNames = (): ReadonlyMap<number, string> => {
  const names = useSourceNamesStore((state) => state.names);
  return useMemo(
    () =>
      new Map(Object.entries(names).map(([id, name]) => [Number(id), name])),
    [names],
  );
};
